import {
    type CreateExtArgs,
    type CreateExtensionArgument,
    type DalJsStore,
    type DeepFunctionMap,
    type DmApis,
    type Extension,
    type ExtensionAPI,
    pointerUtils,
    type SnapshotDal
} from '@wix/document-manager-core'
import type {
    CompRef,
    ConversionAlgoConfigOverrides,
    Pointer,
    ResolvedFunctions,
    BuilderStyle
} from '@wix/document-services-types'
import type {
    AppliedHeuristicLogMessage,
    BaseStageData,
    Stages,
    ConversionAlgoConfig,
    ConversionDal,
    MobileAlgoContext,
    MobileAlgoContextInternalStore,
    MobileAlgoPluginFactory,
    MobileAlgoPluginInitializationArgs,
    MobileAlgoPluginWithContext,
    MobileAlgoRunFunction,
    PluginStageHandler,
    ReadOnlyExtensionAPI,
    Stage,
    StructureIterators
} from './types'
import {StageName, AlgorithmModes, ChildrenProperties, NaryTreeTraversalOrder} from './enums'
import type {DataModelExtensionAPI} from '../dataModel/dataModel'
import _ from 'lodash'
import * as plugins from './plugins'
import {createStructureStage} from './stages/structureStage'
import {createComponentStage} from './stages/componentStage'
import {createPluginContextHelper} from './pluginLifeCycle/pluginContextHelper'
import {createPluginHeuristicsRegistry} from './pluginLifeCycle/pluginHeuristicsRegistry'
import {createPluginContext} from './pluginLifeCycle/pluginContext'
import {createConversionDal} from './conversionDal/conversionDal'
import {createReadOnlyExtensionApi} from './readOnlyExtensionApi'
import type {VariantsExtensionAPI} from '../variants/variants'
import type {GroupApi} from './plugins/grouping/types'
import {sendRequest} from './plugins/grouping/request'
import {DATA_TYPES, VARIANTS, VIEW_MODES} from '../../constants/constants'
import {conversionDataBuilders} from './preprocess'
import {conversionDataTransformers} from './postprocess'
import {mobileConversionValidation} from './validation'
import {createStructureIterators} from './conversionDal/utils/structureIterators'
import {ConversionDalNamespaces} from './conversionDal/constants'
import type {StructureEntryItem, StyleEntryItem} from './conversionDal/types'
import {
    getInnerElementsStylePointer,
    getSourceLayoutPointer,
    getStructurePointer,
    getStylePointer,
    getTargetLayoutPointer,
    getTransformationsPointer
} from './conversionDal/utils/pointer'
import type {PageExtensionAPI} from '../page'
import {getMobileBreakpoint} from './preprocess/utils/mobileBreakPoint'
import {COMPONENT_TYPES} from '../../../testkit/builders/constants'
import type {MeasurementsExtensionAPI} from '../measurements/measurements'
import type {LayoutExtensionApi} from '../layout/layout'
import {removeVariantsFromPointer} from '../../utils/variantsUtils'
import type {InnerElementsExtensionAPI} from '../builderComponents/innerElements'
import {StageRunner} from './stages/stageRunner'

export const DOCUMENT_STAGE_WIDTH = 1280
export const MOBILE_VARIANT = {type: 'variants', id: VARIANTS.MOBILE_VARIANT_ID}

export interface MobileAlgoApi extends ExtensionAPI {
    locking: {
        isComponentLocked(compRef: CompRef): boolean
    }
    context: {
        enable(ctx: MobileAlgoContext, plugins?: string[]): void
        disable(ctx: MobileAlgoContext, plugins?: string[]): void
        create(enabledPlugins?: string[]): MobileAlgoContext
    }
    plugins: {
        register(pluginFactory: MobileAlgoPluginFactory): void
        getApis(ctx: MobileAlgoContext): DeepFunctionMap
    }
    heuristics: HeuristicRegistry
    createConversionConfig(
        containerPointer: CompRef,
        configurationOverrides?: ConversionAlgoConfigOverrides
    ): ConversionAlgoConfig
    writeConversionResult(
        conversionDal: ConversionDal,
        initialSnapshot: SnapshotDal,
        structureIterators: StructureIterators,
        conversionAlgoConfig: ConversionAlgoConfig,
        ctx: MobileAlgoContext
    ): void
    runWithContext(
        containerPointer: CompRef,
        ctx: MobileAlgoContext,
        config: ConversionAlgoConfig
    ): Promise<{
        conversionDal: ConversionDal
        initialSnapshot: SnapshotDal
        structureIterators: StructureIterators
    }>
    run(containerPointer: CompRef, config?: ConversionAlgoConfigOverrides): Promise<void>
    runForAllPages(): Promise<void[][]>
    runForPage(pagePointer: CompRef, config?: ConversionAlgoConfigOverrides): Promise<void[]>
}

export interface HeuristicRegistry extends ExtensionAPI {
    registerHeuristic<T extends BaseStageData>(stage: Stage<T>, handler: PluginStageHandler<T>): void
}

export interface MobileAlgoExtensionApi extends ExtensionAPI {
    mobileAlgo: MobileAlgoApi
}

const createExtension = ({environmentContext, experimentInstance, logger}: CreateExtensionArgument): Extension => {
    const bootPlugins: MobileAlgoPluginFactory[] = Object.values(plugins)

    const createExtensionAPI = ({pointers, dal, extensionAPI}: CreateExtArgs): MobileAlgoExtensionApi => {
        const dataModelApi = () => extensionAPI as DataModelExtensionAPI
        const variantsApi = () => (extensionAPI as VariantsExtensionAPI).variants
        const pages = () => (extensionAPI as PageExtensionAPI).page
        const {components} = extensionAPI as LayoutExtensionApi

        const readOnlyExtensionAPI: ReadOnlyExtensionAPI = createReadOnlyExtensionApi(extensionAPI)
        const stages: Stages = {
            [StageName.HIDE_COMPONENTS]: createComponentStage(),
            [StageName.GROUPING]: createStructureStage(),
            [StageName.GROUPING_ORDER]: createComponentStage({
                structuralType: ConversionDalNamespaces.virtualStructure
            }),
            [StageName.STRUCTURE_ORDER]: createComponentStage(),
            [StageName.HORIZONTAL_LAYOUT_SCALE]: createComponentStage(),
            [StageName.VERTICAL_LAYOUT_SCALE]: createComponentStage({
                traversalOrder: NaryTreeTraversalOrder.PostOrder
            }),
            [StageName.TRANSFORM_STYLE]: createComponentStage(),
            [StageName.POST_PROCESS]: createComponentStage()
        }
        const stageRunner = StageRunner.create(stages)
        const actionsLogger: AppliedHeuristicLogMessage[] = []
        const pluginRegistry = new Map<string, MobileAlgoPluginWithContext>()

        const getPageId = (containerPointer: CompRef) => pointers.structure.getPageOfComponent(containerPointer).id

        const filterPlugins = (pluginsToEnable?: string[]): MobileAlgoPluginWithContext[] => {
            if (!pluginsToEnable) {
                return Object.values(pluginRegistry)
            }

            return Object.values(pluginRegistry).filter(plugin => pluginsToEnable.includes(plugin.name))
        }

        const disablePluginsForContext = (ctx: MobileAlgoContext, pluginsToEnable?: string[]): void => {
            filterPlugins(pluginsToEnable).forEach(plugin => {
                plugin.contextHelper.disable(ctx)
            })
        }

        const enablePluginsForContext = (ctx: MobileAlgoContext, pluginsToEnable?: string[]) => {
            filterPlugins(pluginsToEnable).forEach(plugin => {
                plugin.contextHelper.enable(ctx)

                if (plugin.dependencies) {
                    enablePluginsForContext(ctx, plugin.dependencies as any)
                }
            })
        }

        const getPluginApis = (ctx: MobileAlgoContext): DeepFunctionMap => {
            const result: DeepFunctionMap = {}

            Object.values(pluginRegistry).forEach((plugin: MobileAlgoPluginWithContext) => {
                if (plugin.createApi) {
                    const pluginContext = createPluginContext(plugin.contextHelper, ctx)
                    result[plugin.name] = plugin.createApi(pluginContext)
                }
            })

            return result
        }

        const registerRequest = (ctx: MobileAlgoContext) => {
            const apis = getPluginApis(ctx)
            const {grouping} = apis as GroupApi
            grouping.registerServer(sendRequest)
        }

        const createContextStore = () => {
            const internalStore: MobileAlgoContextInternalStore = {}
            return {
                get: (key: string) => {
                    return internalStore[key]
                },
                set: (key: string, value: any) => {
                    internalStore[key] = value
                }
            }
        }

        const createContext = (enabledPlugins?: string[]): MobileAlgoContext => {
            const ctx: MobileAlgoContext = createContextStore()

            if (enabledPlugins) {
                disablePluginsForContext(ctx)
                enablePluginsForContext(ctx, enabledPlugins)
            } else {
                enablePluginsForContext(ctx)
            }

            registerRequest(ctx)

            return ctx
        }

        const registerPlugin = (pluginFactory: MobileAlgoPluginFactory) => {
            const algoApi = (extensionAPI as MobileAlgoExtensionApi).mobileAlgo
            const initializeArgs: MobileAlgoPluginInitializationArgs = {
                experimentInstance,
                readOnlyExtensionAPI,
                stages,
                environmentContext,
                logger
            }
            const plugin = pluginFactory.createPlugin(initializeArgs)
            const contextHelper = createPluginContextHelper(plugin.name)
            const pluginWithContext = {...plugin, contextHelper}
            const heuristicsRegistry = createPluginHeuristicsRegistry(algoApi.heuristics, contextHelper)
            pluginRegistry[plugin.name] = pluginWithContext
            plugin.register(heuristicsRegistry)
        }

        const registerHeuristic = <T extends BaseStageData>(stage: Stage<T>, handler: PluginStageHandler<T>) => {
            stage.register(handler)
        }

        interface IdAndDepth {
            id: string
            depth: number
        }

        const setFallBackMeasurements = (containerPointer: CompRef, initialStore: DalJsStore) => {
            const fallBackMeasurements = (extensionAPI as MeasurementsExtensionAPI).measurements.generateMeasurement(
                containerPointer
            )

            const queue: IdAndDepth[] = [{id: containerPointer.id, depth: 0}]

            while (queue.length > 0) {
                const {id, depth} = queue.shift()!
                const compPointer = pointerUtils.getPointerFromPointer(id, containerPointer) as CompRef
                const component = dal.get(compPointer)

                initialStore[ConversionDalNamespaces.measurements][id] = fallBackMeasurements[id]
                initialStore[ConversionDalNamespaces.structure][id] = {
                    ...initialStore[ConversionDalNamespaces.structure][id],
                    axisAlignedEnvelope: conversionDataBuilders.createEnvelope(fallBackMeasurements[id], 0)
                }

                for (const childId of component.components ?? []) {
                    queue.push({id: childId, depth: depth + 1})
                }
            }
        }

        const prepareConversionData = (
            containerPointer: CompRef,
            conversionAlgoConfig: ConversionAlgoConfig
        ): DalJsStore => {
            let useFallBack = false

            const queue: IdAndDepth[] = [{id: containerPointer.id, depth: 0}]
            const initialStore: DalJsStore = {
                [ConversionDalNamespaces.structure]: {},
                [ConversionDalNamespaces.measurements]: {},
                [ConversionDalNamespaces.sourceLayout]: {},
                [ConversionDalNamespaces.targetLayout]: {},
                [ConversionDalNamespaces.style]: {},
                [ConversionDalNamespaces.transformations]: {},
                [ConversionDalNamespaces.innerElementsStyle]: {}
            }
            const shouldValidateComponentLayout = experimentInstance.isOpen(
                'dm_shouldValidateComponentLayoutMobileAlgo'
            )

            while (queue.length > 0) {
                const {id, depth} = queue.shift()!
                const compPointer = pointerUtils.getPointerFromPointer(id, containerPointer) as CompRef
                const component = dal.get(compPointer)
                const parentComponent = dal.get({id: component.parent, type: VIEW_MODES.DESKTOP})

                if (shouldValidateComponentLayout) {
                    mobileConversionValidation(component, parentComponent, containerPointer)
                }

                const conversionData = conversionDataBuilders.getConversionData(
                    dal,
                    extensionAPI,
                    pointers,
                    compPointer,
                    conversionAlgoConfig
                )

                for (const [namespace, value] of Object.entries(conversionData)) {
                    initialStore[namespace][id] = value
                }
                if (_.isEmpty(initialStore[ConversionDalNamespaces.measurements][id])) useFallBack = true

                for (const childId of component.components ?? []) {
                    queue.push({id: childId, depth: depth + 1})
                }
            }
            if (useFallBack) setFallBackMeasurements(containerPointer, initialStore)

            return initialStore
        }

        const setLastGroupingHash = (conversionAlgoConfig: ConversionAlgoConfig, lastGroupingHash: string) => {
            const layoutSettings = variantsApi().getComponentItemConsideringVariants(
                conversionAlgoConfig.rootPointer,
                DATA_TYPES.layoutSettings
            )
            variantsApi().updateComponentDataConsideringVariants(
                conversionAlgoConfig.rootPointer,
                {
                    ...layoutSettings,
                    autoResponsivenessSettings: {
                        ...layoutSettings?.autoResponsivenessSettings,
                        lastGroupingsHash: lastGroupingHash
                    },
                    type: 'LayoutSettings'
                },
                DATA_TYPES.layoutSettings
            )
        }

        const writeConversionResult = (
            conversionDal: ConversionDal,
            initialSnapshot: SnapshotDal,
            structureIterators: StructureIterators,
            conversionAlgoConfig: ConversionAlgoConfig,
            ctx: MobileAlgoContext
        ): void => {
            const updateResponsiveLayout = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.targetLayout]?.[compPointer.id]) {
                    return
                }

                variantsApi().updateComponentDataConsideringVariants(
                    compPointer,
                    conversionDal.get(getTargetLayoutPointer(compPointer)),
                    DATA_TYPES.layout
                )
            }

            const updateInnerElementsStyle = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.innerElementsStyle]?.[compPointer.id]) {
                    return
                }

                const {innerElements} = extensionAPI as InnerElementsExtensionAPI

                const innerElementsStyleItem: StyleEntryItem = conversionDal.get(
                    getInnerElementsStylePointer(compPointer)
                )
                const groupedInnerElementStyle =
                    innerElementsStyleItem.fontSize?.reduce((acc, {data, whereToWriteInDM, path, prop}) => {
                        const pathPiped = path!.join('|')
                        return _.merge(acc, {[pathPiped!]: {[whereToWriteInDM]: {[prop]: data}}})
                    }, {}) ?? {}

                Object.entries(groupedInnerElementStyle).forEach(([pathPiped, style]) => {
                    innerElements.setInnerElementStyle(
                        compPointer,
                        pathPiped.split('|'),
                        style as unknown as Partial<BuilderStyle>
                    )
                })
            }

            const updateStyle = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.style]?.[compPointer.id]) {
                    return
                }

                const styleTransformer = conversionDataTransformers.getStyleTransformer(
                    extensionAPI,
                    compPointer,
                    conversionDal.get(getStructurePointer(compPointer)),
                    conversionDal.get(getStylePointer(compPointer))
                )

                if (!styleTransformer) {
                    return
                }

                const styleItem = styleTransformer()

                if (compPointer.variants) {
                    variantsApi().updateComponentDataConsideringVariants(compPointer, styleItem, DATA_TYPES.theme)
                } else {
                    const newStyleItem = dataModelApi().dataModel.addItem(
                        styleItem,
                        DATA_TYPES.theme,
                        pointers.structure.getPageOfComponent(compPointer).id
                    )
                    dataModelApi().dataModel.linkDataToItemByType(compPointer, newStyleItem.id, DATA_TYPES.theme)
                }
            }

            const updateTransformations = (compPointer: CompRef, changes: DalJsStore) => {
                if (!changes[ConversionDalNamespaces.transformations]?.[compPointer.id]) {
                    return
                }

                const transformationsItem = conversionDal.get(getTransformationsPointer(compPointer))
                const {variants} = extensionAPI as VariantsExtensionAPI
                let baseTransformationsItem = variants.getComponentItemConsideringVariants(
                    compPointer,
                    DATA_TYPES.transformations
                )
                if (!baseTransformationsItem) {
                    baseTransformationsItem = dal.schema.createItemAccordingToSchema(
                        'TransformData',
                        DATA_TYPES.transformations
                    )
                }
                const transformationItem = _.merge(baseTransformationsItem, transformationsItem)

                variantsApi().updateComponentDataConsideringVariants(
                    compPointer,
                    transformationItem,
                    DATA_TYPES.transformations
                )
            }

            const writersToExecute = _.over([
                updateResponsiveLayout,
                updateStyle,
                updateInnerElementsStyle,
                updateTransformations
            ])
            const changes = conversionDal.getLastSnapshot()!.diff(initialSnapshot)

            structureIterators.forEachComponent(
                (component: StructureEntryItem) => {
                    const compPointerWithVariants = pointers.getPointer(component.id, VIEW_MODES.DESKTOP, {
                        variants: conversionAlgoConfig.targetVariants
                    })

                    writersToExecute(compPointerWithVariants, changes)
                },
                [ChildrenProperties.components, ChildrenProperties.hiddenComponents]
            )

            const apis = getPluginApis(ctx)
            const {grouping} = apis as GroupApi
            const groupingHashResult = grouping.getAiGroupTree()?.md5 ?? null

            if (groupingHashResult && groupingHashResult !== conversionAlgoConfig.lastGroupingHash) {
                setLastGroupingHash(conversionAlgoConfig, groupingHashResult)
            }
        }

        const getLastGroupingHash = (compRef: CompRef): string | null => {
            return (
                variantsApi().getComponentItemConsideringVariants(compRef, DATA_TYPES.layoutSettings)
                    ?.autoResponsivenessSettings?.lastGroupingsHash ?? null
            )
        }

        const isComponentLocked = (compRef: CompRef): boolean =>
            variantsApi().getComponentItemConsideringVariants(compRef, DATA_TYPES.layoutSettings)
                ?.autoResponsivenessSettings?.mode === AlgorithmModes.Adaptive

        const getMode = (compRef: CompRef): AlgorithmModes =>
            isComponentLocked(compRef) ? AlgorithmModes.Adaptive : AlgorithmModes.Flush

        const getExecutionSettings = (
            compPointer: CompRef
        ): {
            mode: AlgorithmModes
            plugins: string[]
        } => {
            const mode = getMode(compPointer)
            return {
                mode,
                plugins: [
                    ...(mode === AlgorithmModes.Flush && !components.isPinned(removeVariantsFromPointer(compPointer))
                        ? ['grouping']
                        : []),
                    'order',
                    'hide',
                    'alignComponent',
                    'horizontalScaleAndSize',
                    'verticalScaleAndSize',
                    'textScale',
                    'cancelRotation'
                ]
            }
        }

        const createConversionConfig = (
            containerPointer: CompRef,
            configurationOverrides?: ConversionAlgoConfigOverrides
        ): ConversionAlgoConfig => {
            return _.defaults(configurationOverrides, {
                sourceVariants: containerPointer.variants,
                sourceCanvasSize: DOCUMENT_STAGE_WIDTH,
                targetVariants: [MOBILE_VARIANT],
                targetCanvasSize: 390,
                ...getExecutionSettings(
                    pointers.components.getComponentVariantsPointer(
                        containerPointer,
                        configurationOverrides?.targetVariants ?? [MOBILE_VARIANT]
                    ) as CompRef
                ),
                lastGroupingHash: getLastGroupingHash(containerPointer),
                rootPointer: containerPointer,
                debug: false
            })
        }

        const initializeTargetLayout = (
            conversionDal: ConversionDal,
            structureIterators: StructureIterators,
            conversionAlgoConfig: ConversionAlgoConfig
        ): void => {
            structureIterators.forEachComponent((_component, compPointer) => {
                const targetLayoutPointer = getTargetLayoutPointer(compPointer)

                if (conversionDal.get(targetLayoutPointer)) {
                    return
                }

                const sourceLayout = conversionDal.get(getSourceLayoutPointer(compPointer))

                conversionDal.set(targetLayoutPointer, {
                    componentLayout: {
                        hidden: sourceLayout.componentLayout?.hidden || false,
                        type: 'ComponentLayout'
                    },
                    itemLayout: {},
                    containerLayout: {},
                    variableConnections: sourceLayout.variableConnections,
                    spx: {
                        refWidth: conversionAlgoConfig.targetCanvasSize,
                        resolverType: 'scale'
                    },
                    type: 'SingleLayoutData'
                })
            })

            conversionDal.commitChanges('initialize target layout')
        }

        const runWithContext = async (
            containerPointer: CompRef,
            ctx: MobileAlgoContext,
            conversionAlgoConfig: ConversionAlgoConfig
        ) => {
            const conversionData = prepareConversionData(containerPointer, conversionAlgoConfig)
            const conversionDal = createConversionDal(conversionData)
            const initialSnapshot = conversionDal.getLastSnapshot()!
            const structureIterators = createStructureIterators(conversionDal, getStructurePointer(containerPointer))
            const pageId = getPageId(containerPointer)
            initializeTargetLayout(conversionDal, structureIterators, conversionAlgoConfig)

            await stageRunner.run(
                ctx,
                conversionDal,
                initialSnapshot,
                pageId,
                conversionAlgoConfig,
                conversionAlgoConfig.debug ? actionsLogger : undefined
            )

            return {
                conversionDal,
                structureIterators,
                initialSnapshot
            }
        }

        const run = async (compPointer: CompRef, configurationOverrides?: ConversionAlgoConfigOverrides) => {
            const conversionAlgoConfig: ConversionAlgoConfig = createConversionConfig(
                compPointer,
                configurationOverrides
            )
            const ctx = createContext(conversionAlgoConfig.plugins as any)
            const {conversionDal, initialSnapshot, structureIterators} = await runWithContext(
                compPointer,
                ctx,
                conversionAlgoConfig
            )

            writeConversionResult(conversionDal, initialSnapshot!, structureIterators, conversionAlgoConfig, ctx)
        }

        const getPinnedElements = (pagePointer: CompRef): Pointer[] => {
            return pointers.structure
                .getChildren(pagePointer)
                .filter((pointer: Pointer) => (extensionAPI as LayoutExtensionApi).components.isPinned(pointer))
        }

        const runForPage = async (pagePointer: CompRef) => {
            const pageMobileBreakpoint = getMobileBreakpoint(dal, pointers, pagePointer)
            const sectionsInPage = pointers.structure.getComponentsByTypeForPage(
                COMPONENT_TYPES.RESPONSIVE_SECTION,
                pagePointer.id
            )
            const pinnedElementsInPage = getPinnedElements(pagePointer)
            const pageChildren = [...sectionsInPage, ...pinnedElementsInPage]
            return Promise.all(
                pageChildren.map(pointer => {
                    const childMobileBreakpoint = getMobileBreakpoint(dal, pointers, pointer as CompRef)

                    return run(pointer as CompRef, {
                        targetVariants: [(childMobileBreakpoint ?? pageMobileBreakpoint)!]
                    })
                })
            )
        }

        const runForAllPages = async () => {
            const allPagesIds = pages().getAllPagesIds(true)
            return Promise.all(
                allPagesIds.map(pageId => runForPage(pointers.structure.getPage(pageId, VIEW_MODES.DESKTOP)))
            )
        }

        return {
            mobileAlgo: {
                locking: {
                    isComponentLocked
                },
                plugins: {
                    register: registerPlugin,
                    getApis: getPluginApis
                },
                context: {
                    create: createContext,
                    enable: enablePluginsForContext,
                    disable: disablePluginsForContext
                },
                heuristics: {
                    registerHeuristic
                },
                groupings: {
                    getLastGroupingHash
                },
                createConversionConfig,
                runWithContext,
                writeConversionResult,
                run,
                runForAllPages,
                runForPage
            }
        }
    }

    const createPublicAPI = ({extensionAPI}: DmApis) => {
        const {mobileAlgo} = extensionAPI as MobileAlgoExtensionApi

        const wrapAsyncFunction = (fn: MobileAlgoRunFunction) => {
            return async (
                containerPointer: CompRef,
                resolvedFunctions?: ResolvedFunctions,
                config?: ConversionAlgoConfigOverrides
            ) => {
                try {
                    await fn(containerPointer, config)
                    resolvedFunctions?.onSuccess?.()
                } catch (e) {
                    resolvedFunctions?.onError?.()
                }
            }
        }
        return {
            mobileAlgo: {
                run: wrapAsyncFunction(mobileAlgo.run),
                runForPage: wrapAsyncFunction(mobileAlgo.runForPage)
            }
        }
    }

    return {
        name: 'mobileAlgo',
        initialize: async ({extensionAPI}: DmApis) => {
            const mobileAlgoApi = extensionAPI as MobileAlgoExtensionApi
            bootPlugins.forEach(pluginFactory => {
                mobileAlgoApi.mobileAlgo.plugins.register(pluginFactory)
            })
        },
        createExtensionAPI,
        createPublicAPI
    }
}

export {createExtension}
